import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Filters } from '@ui/src';
import {
  Card,
  CardStatus,
  OrderCardFormValues,
  Transaction,
} from '../../types';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
}

export interface CardItemState {
  cardId?: string;
  user?: User;
  cards: {
    isLoading: boolean;
    values?: Card[];
  };
  statusFormValues?: {
    cardId: string;
    status: CardStatus;
  };
  cardInfo?: Card;
  cardEditId?: string;
  orderCardFormValues?: OrderCardFormValues;
  changeStatusInProgress?: boolean;
  changeStatusSuccess?: boolean;
  cardSubmitInProgress?: boolean;
  cardSubmitSuccess?: boolean;
  cardInfoLoading?: boolean;
  transactions: {
    isLoading: boolean;
    values?: Transaction[];
    cardId?: string;
    sort?: {
      sortProp: string;
      sortDir: string;
    };
    filters?: Filters;
    requestInProgress?: boolean;
  };
}

const initialState: CardItemState = {
  user: undefined,
  cardSubmitInProgress: false,
  cardInfoLoading: false,
  cards: {
    isLoading: false,
  },
  transactions: {
    isLoading: false,
  },
};

export const cardItemSlice = createSlice({
  name: 'cardItem',
  initialState,
  reducers: {
    getUser: (state, action: PayloadAction<string>) => {
      state.cardId = action.payload;
    },
    getUserSuccess: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    getCardInfo: (state, action: PayloadAction<string>) => {
      state.cardEditId = action.payload;
      state.cardInfoLoading = true;
    },
    getCardInfoSuccess: (state, action: PayloadAction<Card>) => {
      state.cardInfo = action.payload;
      state.cardInfoLoading = false;
    },
    onSubmitCard: (state, action: PayloadAction<OrderCardFormValues>) => {
      state.orderCardFormValues = action.payload;
      state.cardSubmitInProgress = true;
    },
    onSubmitCardSuccess: (state, action: PayloadAction<Card[]>) => {
      state.cardSubmitInProgress = false;

      state.cards = {
        ...state.cards,
        values: action.payload,
      };

      state.cardSubmitSuccess = true;
    },
    getCards: (state) => {
      state.cards.isLoading = true;
    },
    getCardsSuccess: (state, action: PayloadAction<Card[]>) => {
      state.cards.isLoading = false;
      state.cards.values = action.payload;
    },
    changeStatus: (
      state,
      action: PayloadAction<{ cardId: string; status: CardStatus }>,
    ) => {
      state.statusFormValues = action.payload;
      state.changeStatusInProgress = true;
      state.changeStatusSuccess = false;
    },
    changeStatusSuccess: (state, action: PayloadAction<Card[]>) => {
      state.changeStatusInProgress = false;
      state.cards.values = action.payload;
      state.changeStatusSuccess = true;
    },
    getTransactions: (
      state,
      action: PayloadAction<{
        cardId: string;
        name?: string;
        direction?: 'asc' | 'desc';
        filters?: Filters;
      }>,
    ) => {
      if (!state.transactions.cardId) {
        state.transactions.isLoading = true;
      } else {
        state.transactions.requestInProgress = true;
      }

      state.transactions.cardId = action.payload.cardId;

      if (action?.payload) {
        if (action.payload.name && action.payload.direction) {
          state.transactions.sort = {
            sortDir: action.payload.direction,
            sortProp: action.payload.name,
          };
        }

        if (action.payload.filters) {
          state.transactions.filters = action.payload.filters;
        }
      }
    },
    getTransactionsSuccess: (state, action: PayloadAction<Transaction[]>) => {
      state.transactions.isLoading = false;
      state.transactions.requestInProgress = false;
      state.transactions.values = action.payload;
    },
    onSortTransactions: (
      state,
      action: PayloadAction<{ name: string; direction: 'asc' | 'desc' }>,
    ) => {
      state.transactions.sort = {
        sortDir: action.payload.direction,
        sortProp: action.payload.name,
      };
      state.transactions.requestInProgress = true;
    },
    onSortTransactionsSuccess: (
      state,
      action: PayloadAction<Transaction[]>,
    ) => {
      state.transactions.requestInProgress = false;
      state.transactions.values = action.payload;
    },
    onFilterTransactions: (state, action: PayloadAction<Filters>) => {
      state.transactions.requestInProgress = true;
      state.transactions.filters = action.payload;
    },
    onFilterTransactionsSuccess: (
      state,
      action: PayloadAction<Transaction[]>,
    ) => {
      state.transactions.requestInProgress = false;
      state.transactions.values = action.payload;
    },
    clearCardItemState: (state) => {
      state.cardId = undefined;
      state.user = undefined;
      state.changeStatusSuccess = false;
      state.cards = {
        isLoading: false,
      };
      state.transactions = {
        isLoading: false,
      };
    },
    clearOrderCardForm: (state) => {
      state.orderCardFormValues = undefined;
      state.cardSubmitSuccess = false;
      state.cardEditId = undefined;
      state.cardInfo = undefined;
    },
  },
});

export const {
  getUser,
  getUserSuccess,
  getCards,
  getCardsSuccess,
  changeStatus,
  changeStatusSuccess,
  getTransactionsSuccess,
  getTransactions,
  onSortTransactions,
  onSortTransactionsSuccess,
  onFilterTransactions,
  onFilterTransactionsSuccess,
  clearCardItemState,
  onSubmitCard,
  onSubmitCardSuccess,
  clearOrderCardForm,
  getCardInfo,
  getCardInfoSuccess,
} = cardItemSlice.actions;

export default cardItemSlice.reducer;
